import { createContext, useState, useEffect, useRef, useMemo, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '../config';
import { SocketEvents } from '../constants/socket-events';
import { createLogger } from '../utils/logger';

const log = createLogger('Socket');

type SocketAction =
  | { type: 'CONNECT'; token: string }
  | { type: 'DISCONNECT' }
  | { type: 'RECONNECT'; token?: string };

interface SocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

// Event types that can be subscribed to
export type SocketEventType = 
  | typeof SocketEvents.MESSAGE
  | typeof SocketEvents.STATUS
  | typeof SocketEvents.TYPING
  | typeof SocketEvents.READ
  | typeof SocketEvents.DELIVERY
  | typeof SocketEvents.REACTION
  | typeof SocketEvents.SENDER_LIST_CHANGED
  | typeof SocketEvents.NOTIFICATION
  | typeof SocketEvents.OPERATOR_LOGGED_IN
  | typeof SocketEvents.JOINED
  | typeof SocketEvents.LEFT
  | typeof SocketEvents.ERROR
  | typeof SocketEvents.ERROR_VALIDATION
  | typeof SocketEvents.ERROR_AUTH
  | typeof SocketEvents.ERROR_MESSAGE_SEND
  | typeof SocketEvents.ERROR_MESSAGE_STATUS
  | typeof SocketEvents.ERROR_SYSTEM
  | typeof SocketEvents.ECHO
  | typeof SocketEvents.TOKEN_UPDATE
  | string;

// Event data types
export interface MessageEvent {
  type: 'message';
  channel?: string;
  pageId: string;
  psid: string;
  message: any;
  timestamp?: number;
  id?: string;
}

export interface SenderListChangedEvent {
  type: typeof SocketEvents.SENDER_LIST_CHANGED;
  updatedSender?: {
    psid: string;
    pageId: string;
    channel: string;
    name?: string;
    avatar?: string;
    lastMessageAt?: number;
    lastMessage?: {
      text?: string;
      type?: string;
      timestamp: number;
      dir?: 'inbound' | 'outbound';
    } | null;
    unreadCount?: number;
  };
  senders?: Array<{
    psid: string;
    pageId: string;
    channel: string;
    name?: string;
    avatar?: string;
    lastMessageAt?: number;
    lastMessage?: {
      text?: string;
      type?: string;
      timestamp: number;
      dir?: 'inbound' | 'outbound';
    } | null;
    unreadCount?: number;
  }>;
  updateType?: 'update' | 'full' | 'remove';
  removedSenders?: string[];
  pageId?: string;
  channel?: string;
}

export interface NotificationEvent {
  type: typeof SocketEvents.NOTIFICATION;
  title?: string;
  message: string;
  level?: 'info' | 'warning' | 'error' | 'success';
  data?: any;
  pageId?: string;
  channel?: string;
  alertType?: 'new_message' | 'sender_updated' | 'sender_removed';
}

export type SocketEventData = MessageEvent | SenderListChangedEvent | NotificationEvent | any;

// Event handler type
export type SocketEventHandler = (data: SocketEventData) => void;

export interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  dispatch: (action: SocketAction) => void;
  // Event subscription methods
  on: (event: SocketEventType, handler: SocketEventHandler) => () => void;
  off: (event: SocketEventType, handler: SocketEventHandler) => void;
  emit: (event: string, data?: any) => void;
}

export const SocketContext = createContext<SocketContextType | null>(null);

interface SocketProviderProps {
  children: ReactNode;
  token?: string | null;
  autoConnect?: boolean;
}

/**
 * Events that have a dedicated `newSocket.on(...)` registration inside
 * `setupSocketHandlers`. `onAny` must skip exactly these.
 *
 * Socket.IO's `onAny` fires *in addition to* named listeners, so the previous
 * `eventName.startsWith('event:')` filter double-delivered every `event:*`
 * payload — once from the named listener, once from `onAny` — running each
 * subscribed handler twice per packet.
 *
 * The same prefix filter also silently dropped every event outside the
 * `event:` / `error:` namespaces. `conversation:bump`, `conversation:reopened`
 * and `operator:access:changed` have no named listener AND did not match the
 * prefix, so they never reached `emitToHandlers` at all — meaning
 * `InboxContext`, `PageRoomContext` and `TenantContext` were subscribing to
 * events that could never fire.
 *
 * Skipping the named set and forwarding everything else fixes both, and keeps
 * working for future server events regardless of their namespace.
 */
const EXPLICITLY_FORWARDED_EVENTS = new Set<string>([
  SocketEvents.MESSAGE,
  SocketEvents.STATUS,
  SocketEvents.TYPING,
  SocketEvents.READ,
  SocketEvents.DELIVERY,
  SocketEvents.REACTION,
  SocketEvents.SENDER_LIST_CHANGED,
  SocketEvents.NOTIFICATION,
  SocketEvents.OPERATOR_LOGGED_IN,
  SocketEvents.JOINED,
  SocketEvents.LEFT,
  SocketEvents.TOKEN_UPDATE,
  SocketEvents.ERROR,
  SocketEvents.ERROR_VALIDATION,
  SocketEvents.ERROR_AUTH,
]);

// Helper hook to create a stable callback that always uses the latest values
function useEventCallback<T extends (...args: any[]) => any>(callback: T): T {
  const callbackRef = useRef<T>(callback);
  callbackRef.current = callback;

  return useCallback(
    ((...args: any[]) => callbackRef.current(...args)) as T,
    []
  );
}

/**
 * Base Socket Provider
 * 
 * Handles:
 * - Connection management (connect, disconnect, reconnect)
 * - Authentication (token management)
 * - General event forwarding (all events from all rooms)
 * 
 * For room-specific filtering, use specialized room contexts:
 * - PageRoomContext (filters SENDER_LIST_CHANGED by pageId/channel)
 * - ConversationRoomContext (filters MESSAGE by pageId/channel/psid)
 * - OperatorRoomContext (filters NOTIFICATION for operator)
 */
export function SocketProvider({ 
  children, 
  token, 
  autoConnect = true 
}: SocketProviderProps) {
  const socketRef = useRef<Socket | null>(null);
  const [state, setState] = useState<SocketState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });
  
  // Event handlers registry - stores handlers for each event type
  const eventHandlersRef = useRef<Map<SocketEventType, Set<SocketEventHandler>>>(new Map());

  // Emit event to all registered handlers
  const emitToHandlers = useCallback((event: SocketEventType, data: SocketEventData) => {
    const handlers = eventHandlersRef.current.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          log.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }, []);

  const setupSocketHandlers = useCallback((newSocket: Socket) => {
    newSocket.on('connect', () => {
      log.info('Connected:', newSocket.id);
      setState({
        isConnected: true,
        isConnecting: false,
        error: null,
      });
    });

    newSocket.on('disconnect', (reason) => {
      log.info('Disconnected:', reason);
      setState(prev => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        error: reason === 'io server disconnect' ? 'Server disconnected' : null,
      }));
    });

    newSocket.on('connect_error', (err) => {
      log.error('Connection error:', err.message, err);

      let errorMessage = `Connection error: ${err.message}`;

      if (err.message === 'invalid token' || err.message === 'Authentication failed' || err.message.includes('Authentication')) {
        errorMessage = 'Authentication failed. Please check your token and try again.';
        log.warn('Auth failed - token may be invalid or expired');
      }

      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: errorMessage,
      }));
    });

    newSocket.on('error', (err) => {
      log.error('Error:', err);
      setState(prev => ({
        ...prev,
        error: `Socket error: ${err.message || 'Unknown error'}`,
      }));
    });

    // Forward all events to registered handlers
    // Room-specific contexts will filter these events
    newSocket.on(SocketEvents.MESSAGE, (data: MessageEvent) => {
      emitToHandlers(SocketEvents.MESSAGE, data);
    });

    newSocket.on(SocketEvents.STATUS, (data) => {
      emitToHandlers(SocketEvents.STATUS, data);
    });

    newSocket.on(SocketEvents.TYPING, (data) => {
      emitToHandlers(SocketEvents.TYPING, data);
    });

    newSocket.on(SocketEvents.READ, (data) => {
      emitToHandlers(SocketEvents.READ, data);
    });

    newSocket.on(SocketEvents.DELIVERY, (data) => {
      emitToHandlers(SocketEvents.DELIVERY, data);
    });

    newSocket.on(SocketEvents.REACTION, (data) => {
      emitToHandlers(SocketEvents.REACTION, data);
    });

    newSocket.on(SocketEvents.SENDER_LIST_CHANGED, (data: SenderListChangedEvent) => {
      log.debug('SENDER_LIST_CHANGED received:', {
        event: SocketEvents.SENDER_LIST_CHANGED,
        pageId: data?.pageId,
        channel: data?.channel,
        updateType: data?.updateType,
      });
      emitToHandlers(SocketEvents.SENDER_LIST_CHANGED, data);
    });

    newSocket.on(SocketEvents.NOTIFICATION, (data: NotificationEvent) => {
      emitToHandlers(SocketEvents.NOTIFICATION, data);
    });

    newSocket.on(SocketEvents.OPERATOR_LOGGED_IN, (data) => {
      emitToHandlers(SocketEvents.OPERATOR_LOGGED_IN, data);
    });

    newSocket.on(SocketEvents.JOINED, (data) => {
      emitToHandlers(SocketEvents.JOINED, data);
    });

    newSocket.on(SocketEvents.LEFT, (data) => {
      emitToHandlers(SocketEvents.LEFT, data);
    });

    newSocket.on(SocketEvents.TOKEN_UPDATE, (data) => {
      emitToHandlers(SocketEvents.TOKEN_UPDATE, data);
    });

    // Forward error events
    newSocket.on(SocketEvents.ERROR, (data) => {
      emitToHandlers(SocketEvents.ERROR, data);
    });

    newSocket.on(SocketEvents.ERROR_VALIDATION, (data) => {
      emitToHandlers(SocketEvents.ERROR_VALIDATION, data);
    });

    newSocket.on(SocketEvents.ERROR_AUTH, (data) => {
      emitToHandlers(SocketEvents.ERROR_AUTH, data);
    });

    // Forward every server event that does NOT already have a named listener
    // above. See EXPLICITLY_FORWARDED_EVENTS for why the previous
    // `startsWith('event:')` filter both double-delivered and dropped events.
    // C4 — diagnostic log gated by createLogger('Socket') (off in prod unless
    // VITE_DEBUG_LOG="*" or "Socket").
    newSocket.onAny((eventName, ...args) => {
      if (EXPLICITLY_FORWARDED_EVENTS.has(eventName)) return;

      const data = args[0];
      const subscribers = eventHandlersRef.current.get(eventName)?.size ?? 0;
      if (subscribers === 0) {
        log.debug('forwarded event has no subscribers:', eventName);
      }
      emitToHandlers(eventName as SocketEventType, data);
    });
  }, [emitToHandlers]);

  // Store latest dispatch implementation in ref
  const dispatchRef = useRef<(action: SocketAction) => void>(() => {});
  
  const dispatchImpl = useCallback((action: SocketAction) => {
    switch (action.type) {
      case 'CONNECT': {
        if (socketRef.current?.connected) {
          log.debug('Already connected');
          return;
        }

        setState({
          isConnected: false,
          isConnecting: true,
          error: null,
        });

        const socketUrl = config.socketServer;
        log.info('Connecting to:', socketUrl);

        const newSocket = io(socketUrl, {
          transports: ['websocket', 'polling'],
          auth: { token: action.token },
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 45000,
        });

        setupSocketHandlers(newSocket);
        socketRef.current = newSocket;
        break;
      }

      case 'DISCONNECT': {
        if (socketRef.current) {
          log.info('Disconnecting...');
          socketRef.current.disconnect();
          socketRef.current = null;
          setState({
            isConnected: false,
            isConnecting: false,
            error: null,
          });
        }
        break;
      }

      case 'RECONNECT': {
        if (socketRef.current) {
          if (action.token) {
            socketRef.current.auth = { token: action.token };
          }
          socketRef.current.connect();
        } else if (action.token) {
          dispatchRef.current?.({ type: 'CONNECT', token: action.token });
        }
        break;
      }
    }
  }, [setupSocketHandlers]);

  // Update ref with latest implementation
  dispatchRef.current = dispatchImpl;

  // Stable dispatch function that always calls the latest implementation
  const dispatch = useEventCallback((action: SocketAction) => {
    dispatchRef.current(action);
  });

  // Auto-connect when token is provided
  useEffect(() => {
    if (autoConnect && token && !socketRef.current) {
      log.debug('Auto-connecting with token…');
      dispatch({ type: 'CONNECT', token });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, autoConnect]);

  // Track previous token to detect changes (not initial connection)
  const prevTokenRef = useRef<string | null | undefined>(token);

  // Emit token update event when token changes and socket is connected
  useEffect(() => {
    const prevToken = prevTokenRef.current;
    prevTokenRef.current = token;

    // Only emit if:
    // 1. Socket is connected
    // 2. Token exists
    // 3. Token actually changed (not initial connection)
    if (token && socketRef.current?.connected && prevToken !== undefined && prevToken !== token) {
      log.debug('Token changed, sending token update event…');
      socketRef.current.emit(SocketEvents.TOKEN_UPDATE, { token });
    }
  }, [token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Event subscription methods
  const on = useCallback((event: SocketEventType, handler: SocketEventHandler) => {
    if (!eventHandlersRef.current.has(event)) {
      eventHandlersRef.current.set(event, new Set());
    }
    eventHandlersRef.current.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = eventHandlersRef.current.get(event);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          eventHandlersRef.current.delete(event);
        }
      }
    };
  }, []);

  const off = useCallback((event: SocketEventType, handler: SocketEventHandler) => {
    const handlers = eventHandlersRef.current.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        eventHandlersRef.current.delete(event);
      }
    }
  }, []);

  const emit = useCallback((event: string, data?: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else {
      log.warn('Cannot emit event - socket not connected', { event });
    }
  }, []);

  // Context value
  const value: SocketContextType = useMemo(
    () => ({
      socket: socketRef.current,
      isConnected: state.isConnected,
      isConnecting: state.isConnecting,
      error: state.error,
      dispatch,
      on,
      off,
      emit,
    }),
    [state.isConnected, state.isConnecting, state.error, dispatch, on, off, emit]
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}
